/**
 * Auth subdomain detection and URL utilities.
 *
 * Production:  auth.v2.meitre.com
 * Staging:     auth.meitre-test.com
 * Local dev:   localhost:8080?auth=1
 */

import { getCurrentDomainConfig } from '@core/tenant/getSubdomainPrefix';
import { safeSessionStorage } from '@shared/utils/safeStorage';

/**
 * Check if the current hostname is the auth subdomain.
 */
export function isAuthSubdomain(): boolean {
  if (import.meta.env.DEV) {
    return new URLSearchParams(window.location.search).has('auth');
  }
  return window.location.hostname.startsWith('auth.');
}

/**
 * Get the auth domain for the current environment.
 * Resolved from DOMAIN_CONFIG so all domain knowledge lives in one place.
 *
 * pepito.v2.meitre.com   → auth.v2.meitre.com
 * pepito.meitre.com      → auth.v2.meitre.com
 * pepito.meitre-test.com → auth.meitre-test.com
 * localhost               → localhost:8080 (uses ?auth=1)
 */
export function getAuthDomain(): string {
  if (import.meta.env.DEV) {
    return window.location.host; // localhost:8080
  }

  const config = getCurrentDomainConfig();
  if (config) return config.authDomain;

  // Fallback: derive from hostname
  const parts = window.location.hostname.split('.');
  parts[0] = 'auth';
  return parts.join('.');
}

/**
 * Get the full URL for the auth subdomain.
 *
 * In dev mode, appends `?auth=1` instead of changing the domain.
 */
export function getAuthUrl(
  flow: 'login' | 'signup',
  params: Record<string, string>,
): string {
  const protocol = window.location.protocol;
  const domain = getAuthDomain();
  const searchParams = new URLSearchParams(params);

  if (import.meta.env.DEV) {
    searchParams.set('auth', '1');
  }

  return `${protocol}//${domain}/${flow}?${searchParams}`;
}

const TRUSTED_HOST_SUFFIXES = ['.v2.meitre.com', '.meitre.com', '.meitre-test.com', '.meitre-staging.com'];

export function isTrustedReturnUrl(candidate: string): boolean {
  if (!candidate) return false;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  if (parsed.protocol === 'http:') {
    if (!import.meta.env.DEV || (host !== 'localhost' && host !== '127.0.0.1')) return false;
    return true;
  }
  if (parsed.protocol !== 'https:') return false;
  if (host === 'meitre.com' || host === 'meitre-test.com' || host === 'meitre-staging.com') return true;
  return TRUSTED_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

/**
 * Get the restaurant subdomain from the current URL.
 * Returns the first part of the hostname (e.g. "pepito" from "pepito.v2.meitre.com").
 * In dev mode reads from ?r= or sessionStorage.
 */
export function getRestaurantSubdomain(): string {
  if (import.meta.env.DEV) {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('r');
    if (fromQuery) return fromQuery;
    return safeSessionStorage.getItem('restaurant_slug') || '';
  }
  return window.location.hostname.split('.')[0];
}
